import React, { useEffect, useRef } from 'react';
import queryString from 'query-string';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import classes from './classes.module.scss';
import { chatTypeToOrderType, purchaseTypeToOrderType } from '../../../config/const';
import { toCamelCase } from '../../../../common/config/utils';
import { SvgIcon } from '../../../../common/sprites';
import AddApplePayOption from '../../../../common/payment_options/apple_pay/add_apple_pay_option';
import { applePayIsSupported } from '../../../../common/purchase_details_common/purchase_details_common_utils';

const getBuyBtnClassName = ({ loading, disableBuyButton }) => {
  if (loading) return classnames(classes.creditCardValidateBuyBtnContainer, classes.skeleton);
  if (disableBuyButton) {
    return classnames(classes.creditCardValidateBuyBtnContainer, classes.creditCardValidateDisableBuyButton);
  }
  return classes.creditCardValidateBuyBtnContainer;
};

const renderError = ({ t, error }) => {
  if (!error) return null;
  return (
    <div className={ classes.creditCardValidateErrorContainer }>
      <div className={ classes.creditCardValidateErrorImgContainer }>
        <SvgIcon id="error_icon" className={ classes.creditCardValidateErrorImg } />
      </div>
      <div className={ classes.creditCardValidateErrorTitle }>
        { t('credit_card_validate_processing.card_validation_failed') }
      </div>
      <div className={ classes.creditCardValidateErrorText }>
        { t('credit_card_validate_processing.there_was_a_failure_validating_your_card') }
      </div>
    </div>
  );
};

const getValidateCreditCardRequestParams = ({ advisorId, chatType }) => {
  const {
    intendedAdvisorId, purchaseType
  } = toCamelCase(queryString.parse(window.location.search));
  const requestParams = {
    intended_advisor_id: parseFloat(intendedAdvisorId) || advisorId,
    order_type: purchaseTypeToOrderType[purchaseType] || chatTypeToOrderType[chatType]
  };
  return requestParams;
};

function CreditCardValidateProcessing({
  disableBuyButton,
  loading,
  onCreditCardValidated,
  validateCreditCardAction,
  validatedData,
  error,
  validateCreditCardClearAction,
  getValidateCreditCardStatus,
  creditCardValidateProcessingValidating,
  requiresPayAsYouGoValidation,
  advisorId,
  chatType,
  currentPaymentSource,
  paymentMethodListAnalyticsEvents
}) {
  const { t } = useTranslation();

  const getValidateCreditCardStatusIntervalRef = useRef(null);

  useEffect(() => {
    if (validatedData) {
      const { id, state } = validatedData || {};
      if (state === 'pending' && !getValidateCreditCardStatusIntervalRef.current) {
        const int = setInterval(() => {
          getValidateCreditCardStatus({ id });
        }, 1500);
        getValidateCreditCardStatusIntervalRef.current = int;
        return () => {
          clearInterval(int);
          getValidateCreditCardStatusIntervalRef.current = null;
        };
      }
      if (state !== 'pending' || error) {
        clearInterval(getValidateCreditCardStatusIntervalRef.current);
        getValidateCreditCardStatusIntervalRef.current = null;
        creditCardValidateProcessingValidating({ validating: false });
      }
      if (state === 'validated') {
        onCreditCardValidated();
      }
    }
    return () => {};
  }, [validatedData]);

  useEffect(() => () => {
    clearInterval(getValidateCreditCardStatusIntervalRef.current);
    getValidateCreditCardStatusIntervalRef.current = null;
    validateCreditCardClearAction();
  }, []);

  const validateCreditCardActionHandler = () => {
    const requestParams = getValidateCreditCardRequestParams({ advisorId, chatType });
    validateCreditCardAction({ requestParams });
  };

  const getCreditCardValidateBtnData = () => {
    let onClickBtn = onCreditCardValidated;
    let text = t('purchase.purchase_start_session');
    if (requiresPayAsYouGoValidation) {
      onClickBtn = validateCreditCardActionHandler;
    }
    if (error) {
      text = t('general.ok');
      onClickBtn = validateCreditCardClearAction;
    }
    return { onClickBtn, text };
  };

  const renderCreditCardValidateBtn = () => {
    const { onClickBtn, text } = getCreditCardValidateBtnData();

    let creditCardValidateBtn = null;

    creditCardValidateBtn = (
      <button type="button" onClick={ onClickBtn } className={ getBuyBtnClassName({ loading, disableBuyButton, error  }) }>
        { text }
      </button>
    );

    if (currentPaymentSource?.kind === 'am' && currentPaymentSource?.requiresConfiguration && applePayIsSupported()) {
      creditCardValidateBtn =  (
        <div className={ getBuyBtnClassName({ loading, disableBuyButton, error  }) } style={ { height: '44px', backgroundColor: 'unset' } }>
          <AddApplePayOption kind={ currentPaymentSource.kind } context={ currentPaymentSource.context } analyticsEvents={ paymentMethodListAnalyticsEvents } onApplePayValidatedAction={ onClickBtn } loading={ loading } />
        </div>
      );
    }

    return (
      <div className={ classes.validateCardButtonContainer }>
        { creditCardValidateBtn }
      </div>
    );
  };

  const renderCreditCardValidateProcessing = () => (
    <div className={ classes.creditCardValidateContainer }>
      { renderError({ t, error }) }
      { renderCreditCardValidateBtn() }
    </div>
  );

  return renderCreditCardValidateProcessing();
}

export default CreditCardValidateProcessing;
